import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, Clock, DollarSign, ShieldCheck, LineChart, ArrowRight } from 'lucide-react';
import { initialModules, SavingsType } from '@/data/modules';
import { calculateCostSavingsForSubmodule } from '@/utils/savingsCalculations';

const FeaturedProcesses = () => {
  const navigate = useNavigate();

  // Flatten all submodules into a single array with additional properties
  const allSubmodules = initialModules.flatMap(module => 
    module.subModules.map(submodule => ({
      ...submodule,
      uniqueId: `${module.id}-${submodule.id}`,
      moduleName: module.name,
      moduleId: module.id
    }))
  );
  
  // Get featured submodules (top 12 by efficiency * originalHours)
  const featuredSubmodules = allSubmodules
    .filter(submodule => submodule.default || submodule.efficiency > 60)
    .sort((a, b) => b.originalHours * b.efficiency - a.originalHours * a.efficiency)
    .slice(0, 8); // Show top 8 processes
  
  const handleExploreSubmodule = (moduleId: string, submoduleId: string) => {
    navigate(`/customize?module=${moduleId}&submodule=${submoduleId}`);
  };
  
  // Helper function to get savings icon based on savings type
  const getSavingsIcon = (savingsType: SavingsType) => {
    switch(savingsType) {
      case SavingsType.TIME:
        return <Clock className="h-5 w-5 text-blue-600" />;
      case SavingsType.COST:
        return <DollarSign className="h-5 w-5 text-blue-600" />;
      case SavingsType.ERROR:
        return <ShieldCheck className="h-5 w-5 text-purple-600" />;
      case SavingsType.ROI:
        return <LineChart className="h-5 w-5 text-amber-600" />;
      default:
        return null;
    }
  };
  
  // Helper function to get color class based on savings type
  const getSavingsColorClass = (savingsType: SavingsType) => {
    switch(savingsType) {
      case SavingsType.TIME:
        return "bg-blue-50 border-blue-200";
      case SavingsType.COST:
        return "bg-blue-50 border-blue-200";
      case SavingsType.ERROR:
        return "bg-purple-50 border-purple-200";
      case SavingsType.ROI:
        return "bg-amber-50 border-amber-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  return (
    <section className="w-full h-full flex flex-col justify-center bg-white">
      <div className="container mx-auto px-4 max-w-7xl h-full py-8">

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {featuredSubmodules.map((submodule) => {
            const costSavings = calculateCostSavingsForSubmodule(submodule);
            
            return (
              <Card 
                key={submodule.uniqueId} 
                className={`border hover:shadow-lg transition-all cursor-pointer ${getSavingsColorClass(submodule.linkToSavings)}`}
                onClick={() => handleExploreSubmodule(submodule.moduleId, submodule.id)}
              >
                <CardHeader className="p-3 pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {getSavingsIcon(submodule.linkToSavings)}
                      <span className="text-xs text-slate-500 truncate max-w-[100px]">{submodule.moduleName}</span>
                    </div>
                    <div className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">
                      {submodule.efficiency}% faster
                    </div>
                  </div>
                  <CardTitle className="text-sm mt-1.5 line-clamp-2">{submodule.name}</CardTitle>
                </CardHeader>
                
                <CardContent className="p-3 pt-0">
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs">{submodule.steps.length}→{submodule.steps_with_ai.length} steps</span>
                    <span className="text-xs font-medium text-blue-700">€{costSavings.toFixed(0)}/yr</span>
                  </div>
                  
                  <div className="flex w-full justify-between items-center mt-2">
                    <div className="flex gap-0.5">
                      {Array(5).fill(0).map((_, i) => (
                        <div 
                          key={`${submodule.uniqueId}-impl-${i}`}
                          className={`w-1.5 h-1.5 rounded-full ${i < submodule.implementation ? 'bg-purple-600' : 'bg-gray-200'}`} 
                        />
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs font-medium"
                    >
                      View
                      <ChevronRight className="ml-0.5 h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        <div className="text-center">
          <Button 
            onClick={() => navigate('/learn-more')} 
            className="bg-blue-600 hover:bg-blue-500 text-white px-6 rounded-lg shadow-sm hover:shadow transition-all duration-300"
            size="lg"
          >
            Explore All Processes
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProcesses;